import { useQuery } from "@tanstack/react-query";
import { Bell, CheckCircle2, Clock, Users, XCircle } from "lucide-react";
import React, { useMemo, useState } from "react";
import type { WorkflowStage } from "../../api/mappers/approvalWorkflowMapper";
import type { ScenarioApprovalViewNode } from "../../api/models/scenarioApprovalView";
import { getWorkflowStagesGraphql } from "../../api/services/approvalWorkflowGraphqlService";
import { getScenarioApprovalsGraphql } from "../../api/services/scenarioGraphqlService";
import { useActions } from "../../contexts/ActionsContext";
import { useApp } from "../../contexts/AppContext";
import { cn, formatDate } from "../../lib/utils";
import {
  ApprovalStatus,
  ComprehensiveScenario,
  ScenarioApproval,
} from "../../types/scenarios";
import { CreateActionModal } from "../actions/CreateActionModal";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

interface ViewApproversModalProps {
  open: boolean;
  onClose: () => void;
  scenario: ComprehensiveScenario;
}

interface ApprovalStage {
  order: number;
  name: string;
  approvers: ScenarioApproval[];
}

// Demo mode only — matches DEMO_WORKFLOW_STAGES (Stage 1: CFO, Stage 2: CEO).
const DEMO_STAGE_NAMES = ["Finance Review", "CEO Sign-off"];

/** The REST mutations persist the decision verbatim; map it onto the UI status union. */
function statusFromDecision(decision: string): ApprovalStatus {
  const value = decision.toLowerCase();
  if (value === "approved" || value === "approve") return "approved";
  if (value === "rejected" || value === "reject") return "rejected";
  if (value === "changes-requested" || value === "request-changes")
    return "changes-requested";
  return "pending";
}

function decisionToApproval(
  decision: ScenarioApprovalViewNode,
): ScenarioApproval {
  return {
    id: `decision-${decision.id}`,
    userId: decision.userId,
    approverId: decision.userId,
    userName: decision.userName ?? decision.userId,
    userRole: decision.stageName ?? "",
    status: statusFromDecision(decision.decision),
    comments: decision.comments ?? undefined,
    timestamp: new Date(decision.dateCreated),
    order: decision.stageOrder,
  };
}

/**
 * Overlay the recorded decisions onto the workflow's stage structure: a stage shows one
 * row per decision, plus a pending row for every assignee who has not yet decided.
 */
function buildLiveStages(
  stages: WorkflowStage[],
  decisions: ScenarioApprovalViewNode[],
): ApprovalStage[] {
  return stages.map((stage) => {
    const stageDecisions = decisions.filter(
      (d) => d.stageOrder === stage.order,
    );
    const decidedUserIds = new Set(stageDecisions.map((d) => d.userId));

    const pending: ScenarioApproval[] = stage.assignees
      .filter(
        (assignee) => !assignee.userId || !decidedUserIds.has(assignee.userId),
      )
      .map((assignee) => ({
        id: `pending-${stage.id}-${assignee.id}`,
        userId: assignee.userId ?? assignee.id,
        approverId: assignee.userId ?? assignee.id,
        userName: assignee.name,
        userRole: stage.name,
        status: "pending" as ApprovalStatus,
        timestamp: new Date(),
        order: stage.order,
      }));

    return {
      order: stage.order,
      name: stage.name,
      approvers: [...stageDecisions.map(decisionToApproval), ...pending],
    };
  });
}

/** Demo mode keeps the in-memory approval chain grouped into the two demo stages. */
function buildDemoStages(chain: ScenarioApproval[]): ApprovalStage[] {
  return DEMO_STAGE_NAMES.map((name, index) => ({
    order: index + 1,
    name,
    approvers: chain.filter((a) => a.order === index + 1),
  }));
}

export const ViewApproversModal: React.FC<ViewApproversModalProps> = ({
  open,
  onClose,
  scenario,
}) => {
  const [nudgeApprover, setNudgeApprover] = useState<ScenarioApproval | null>(
    null,
  );
  const { createAction } = useActions();
  const { mode } = useApp();
  const isLive = mode === "live";

  const currentStage = scenario.currentStageOrder ?? 1;
  const chain = scenario.approvalChain ?? [];
  const workflowId = scenario.approvalWorkflowId ?? null;

  const { data: workflowStages } = useQuery({
    queryKey: ["approvalWorkflowStages", workflowId],
    queryFn: () => getWorkflowStagesGraphql(workflowId as number),
    enabled: open && isLive && workflowId != null,
  });

  const { data: decisions } = useQuery({
    queryKey: ["scenarioApprovals", scenario.id],
    queryFn: () => getScenarioApprovalsGraphql(Number(scenario.id)),
    enabled: open && isLive,
  });

  const stages = useMemo<ApprovalStage[]>(
    () =>
      isLive
        ? buildLiveStages(workflowStages ?? [], decisions ?? [])
        : buildDemoStages(chain),
    [isLive, workflowStages, decisions, chain],
  );

  const totalStages = stages.length;

  // Approvers at the current stage that have not yet actioned.
  const pendingCount = (
    stages.find((s) => s.order === currentStage)?.approvers ?? []
  ).filter((a) => a.status === "pending").length;

  const getStatusIcon = (status: ScenarioApproval["status"]) => {
    if (status === "approved")
      return (
        <CheckCircle2 className="h-4 w-4 text-[var(--accent-success-500)]" />
      );
    if (status === "rejected")
      return (
        <XCircle className="h-4 w-4 text-[var(--accent-destructive-500)]" />
      );
    return <Clock className="h-4 w-4 text-[var(--accent-warning-500)]" />;
  };

  const getStatusLabel = (status: ScenarioApproval["status"]) => {
    if (status === "approved") return "Approved";
    if (status === "rejected") return "Rejected";
    if (status === "changes-requested") return "Changes Requested";
    return "Awaiting Action";
  };

  const getStatusBadgeClass = (status: ScenarioApproval["status"]) => {
    if (status === "approved")
      return "bg-[var(--accent-success-50)] text-[var(--accent-success-900)] border-[var(--accent-success-300)]";
    if (status === "rejected")
      return "bg-[var(--accent-destructive-50)] text-[var(--accent-destructive-900)] border-[var(--accent-destructive-300)]";
    return "bg-[var(--accent-warning-50)] text-[var(--accent-warning-900)] border-[var(--accent-warning-300)]";
  };

  const renderApproverRow = (
    approver: ScenarioApproval,
    isCurrentStage: boolean,
  ) => {
    const isPending = approver.status === "pending";
    const isActioned = !isPending;

    return (
      <div
        key={approver.id}
        className={cn(
          "flex items-center justify-between p-3 rounded-lg border",
          isActioned
            ? "bg-gray-50 border-gray-100"
            : isCurrentStage
              ? "bg-white border-[var(--accent-warning-200)]"
              : "bg-gray-50 border-gray-100 opacity-60",
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          {getStatusIcon(approver.status)}
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {approver.userName}
            </div>
            <div className="text-xs text-gray-500">{approver.userRole}</div>
            {isActioned && approver.timestamp && (
              <div className="text-xs text-gray-400 mt-0.5">
                {getStatusLabel(approver.status)} on{" "}
                {formatDate(approver.timestamp)}
              </div>
            )}
            {isActioned && approver.comments && (
              <div className="text-xs text-gray-500 mt-1 italic line-clamp-2">
                "{approver.comments}"
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {isPending && isCurrentStage ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              onClick={() => setNudgeApprover(approver)}
            >
              <Bell className="h-3 w-3" />
              Nudge
            </Button>
          ) : (
            <Badge
              variant="outline"
              className={cn(
                "text-xs font-medium",
                getStatusBadgeClass(approver.status),
              )}
            >
              {getStatusLabel(approver.status)}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const nudgeActionTitle = nudgeApprover
    ? `Approval requested: ${scenario.name}`
    : "";
  const nudgeActionDescription = nudgeApprover
    ? `Hi ${nudgeApprover.userName.split(" ")[0]}, could you please review and action the approval for scenario "${scenario.name}"? It is currently at Stage ${currentStage} of ${totalStages} and awaiting your sign-off. Thank you.`
    : "";

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[var(--accent-warning-500)]" />
              Approval Status
            </DialogTitle>
          </DialogHeader>

          {/* Scenario name */}
          <p className="text-sm text-gray-500 -mt-1 truncate">
            {scenario.name}
          </p>

          {/* Status summary banner */}
          <div className="flex items-center gap-3 p-3 bg-[var(--accent-warning-50)] border border-[var(--accent-warning-200)] rounded-lg">
            <Clock className="h-4 w-4 text-[var(--accent-warning-600)] flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-[var(--accent-warning-900)]">
                Awaiting Approval
              </span>
              <span className="text-[var(--accent-warning-700)]">
                {" "}
                — Stage {currentStage}
                {totalStages > 0 && ` of ${totalStages}`}
                {pendingCount > 0 &&
                  ` · ${pendingCount} approval${pendingCount !== 1 ? "s" : ""} still required`}
                {pendingCount === 0 && " · All approvals received"}
              </span>
            </div>
          </div>

          {stages.length === 0 && (
            <div className="text-xs text-gray-400 italic px-3">
              No approval stages configured for this scenario
            </div>
          )}

          {stages.map((stage) => (
            <div key={stage.order} className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Stage {stage.order} — {stage.name}
                </span>
                {stage.order === currentStage && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-[var(--accent-warning-50)] text-[var(--accent-warning-700)] border-[var(--accent-warning-300)]"
                  >
                    Current Stage
                  </Badge>
                )}
                {stage.order < currentStage && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-[var(--accent-success-50)] text-[var(--accent-success-700)] border-[var(--accent-success-300)]"
                  >
                    Complete
                  </Badge>
                )}
                {stage.order > currentStage && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-gray-100 text-gray-500 border-gray-200"
                  >
                    Pending
                  </Badge>
                )}
              </div>
              {stage.approvers.length > 0 ? (
                stage.approvers.map((a) =>
                  renderApproverRow(a, stage.order === currentStage),
                )
              ) : (
                <div className="text-xs text-gray-400 italic px-3">
                  No approver assigned
                </div>
              )}
            </div>
          ))}

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-filled nudge action modal */}
      {nudgeApprover && (
        <CreateActionModal
          open={!!nudgeApprover}
          onClose={() => setNudgeApprover(null)}
          onConfirm={(payload) => {
            createAction(payload);
            setNudgeApprover(null);
          }}
          prefill={{
            title: nudgeActionTitle,
            description: nudgeActionDescription,
            assignedTo: nudgeApprover.userId,
          }}
        />
      )}
    </>
  );
};
